import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';

const WhoWeAreSection = () => {
  const containerRef = useRef(null);

  // Scroll Progress track karne ke liye
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"]
  });

  // --- Animation Logic (Same as before) ---
  const yMission = useTransform(scrollYProgress, [0, 0.5], ["0%", "-50%"]);
  const opacityMission = useTransform(scrollYProgress, [0.3, 0.5], [1, 0]);
  const scaleMission = useTransform(scrollYProgress, [0.3, 0.5], [1, 0.8]);

  const yVision = useTransform(scrollYProgress, [0.2, 0.7], ["120%", "0%"]);
  const opacityVision = useTransform(scrollYProgress, [0.2, 0.5], [0, 1]);

  return (
    <section ref={containerRef} className="relative h-[150vh] sm:h-[180vh] md:h-[200vh] bg-[#fffcf5] py-8 md:py-0">
      {/* Sticky Container */}
      <div className="sticky top-0 min-h-screen md:h-screen flex items-center overflow-hidden">
        {/* Left Side Background - Hidden on mobile */}
        <div className="hidden lg:block absolute top-0 left-0 w-1/3 h-full bg-gray-100/50 skew-x-12 translate-x-20 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-6 w-full">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8 md:gap-12 lg:gap-16">
            
            {/* --- LEFT SIDE: STATIC TEXT --- */}
            <div className="w-full lg:w-1/2 space-y-6 md:space-y-8 z-10">
              <div className="space-y-3 md:space-y-4">                
                <motion.h2 
                  initial={{ opacity: 0, y: -20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                  className="text-3xl sm:text-4xl md:text-5xl text-left font-semibold text-black"
                >
                  Who We Are
                </motion.h2>
                <motion.div 
                  initial={{ width: 0 }}
                  whileInView={{ width: 100 }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                  className="h-1 bg-[#2a3890] rounded-full"
                />
              </div>
              
              <p className="text-base sm:text-lg md:text-xl text-left text-gray-600 leading-relaxed max-w-xl">
                TM International Group is a global holding company with strategic investments in construction, development, and real estate across multiple markets. With over 16 years of operational excellence, the group manages a diverse portfolio of subsidiary companies, delivering more than 500 successful projects across a wide range of large-scale developments and high-value assets.
              </p>
            </div>

            {/* --- RIGHT SIDE: ANIMATED CARDS --- */}
            <div className="w-full lg:w-1/2 h-[400px] sm:h-[450px] md:h-[500px] relative flex items-center justify-center mt-8 lg:mt-0">
              
              {/* CARD 1: OUR MISSION */}
              <motion.div 
                style={{ y: yMission, opacity: opacityMission }}
                className="absolute w-full max-w-md h-[380px] sm:h-[420px] md:h-[450px] overflow-hidden shadow-2xl z-10 border border-white/20"
              >
                {/* Background Image: Construction Site */}
                <div className="absolute inset-0">
                  <img 
                    src="https://images.unsplash.com/photo-1541888946425-d81bb19240f5?q=80&w=2070&auto=format&fit=crop" 
                    alt="Construction Site" 
                    className="w-full h-full object-cover"
                  />
                  {/* Dark Blue Overlay */}
                  <div className="absolute inset-0 bg-[#0f172a]/40 mix-blend-multiply" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                </div>

                {/* Content */}
                <div className="relative z-10 h-full p-6 sm:p-8 md:p-10 flex flex-col justify-center items-center text-center">
                  {/* Icon */}
                  {/* <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 mb-4 sm:mb-5 md:mb-6 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center border border-white/20">
                     <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 sm:h-7 sm:w-7 md:h-8 md:w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                     </svg>
                  </div> */}

                  <h3 className="text-2xl sm:text-2xl md:text-3xl font-bold text-white mb-3 sm:mb-4 tracking-wide">OUR MISSION</h3>
                  <div className="w-20 h-1 bg-[#2a3890] rounded-full mb-4 sm:mb-5 md:mb-6"></div>
                  <p className="text-gray-200 text-sm sm:text-base md:text-lg leading-relaxed font-light px-2">
                    To deliver exceptional construction and development solutions that exceed client expectations 
                    while maintaining the highest standards of quality, safety, and sustainability. 
                  </p>
                </div>
              </motion.div>

              {/* CARD 2: OUR VISION */}
              <motion.div 
                style={{ y: yVision, opacity: opacityVision }}
                className="absolute w-full max-w-md h-[380px] sm:h-[420px] md:h-[450px] overflow-hidden shadow-2xl z-20 border border-white/20"
              >
                 {/* Background Image: Skyscraper/Modern */}
                 <div className="absolute inset-0">
                  <img 
                    src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?q=80&w=2070&auto=format&fit=crop" 
                    alt="Modern Architecture" 
                    className="w-full h-full object-cover"
                  />
                  {/* Gold/Bronze Overlay */}
                  <div className="absolute inset-0 bg-[#6d5b38]/40 mix-blend-multiply" />
                  <div className="absolute inset-0 bg-gradient-to-br from-[#9a8456]/10 to-black/50" />
                </div>

                {/* Content */}
                <div className="relative z-10 h-full p-6 sm:p-8 md:p-10 flex flex-col justify-center items-center text-center">
                   {/* Icon */}
                   {/* <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 mb-4 sm:mb-5 md:mb-6 rounded-full backdrop-blur-sm flex items-center justify-center border border-[#9a8456]/40">
                     <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 sm:h-7 sm:w-7 md:h-8 md:w-8 text-[#f0e6d2]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                     </svg>
                  </div> */}

                  <h3 className="text-2xl sm:text-2xl md:text-3xl font-bold text-[#f0e6d2] mb-3 sm:mb-4 tracking-wide">OUR VISION</h3>
                  <div className="w-20 h-1 bg-[#9a8456] rounded-full mb-4 sm:mb-5 md:mb-6"></div>
                  <p className="text-gray-100 text-sm sm:text-base md:text-lg leading-relaxed font-light px-2">
                    To be the premier choice for construction and development services globally, recognized for our innovation, 
                    integrity, and commitment to excellence. 
                  </p>
                </div>
              </motion.div>

            </div>
            
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhoWeAreSection;